import React from 'react';
import { Helmet } from 'react-helmet-async';

import i18n from '../../i18n';
// @mui
import {
    Button,
    ButtonGroup,
    Card,
    Table,
    Stack,
    Checkbox,
    Container,
    IconButton,
    MenuItem,
    Popover,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    Tooltip,
    Skeleton,
    Box,
    Typography
} from '@mui/material';
import {
    SearchOff as SearchOffIcon,
    FilterAltOff as FilterAltOffIcon,
    Inbox as InboxIcon
} from '@mui/icons-material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
// sections
import { GridListHead, GridListToolbar } from '../../sections/@dashboard/grid';

import DialogAbstract from './DialogAbstract';
import Cells from '../cells';
//utils
import { adaptedName } from '../../utils/dataHelpers';
import TableSettingsDialog from '../dialogs/TableSettingsDialog';
import DetailsDialogAbstract from '../dialogs/DetailsDialog/DetailsDialogAbstract';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import config from '../../config';
import { formatFilterData, parseDate } from '../../utils/formatCalendar';
import DateTimePickerDialog from '../dialogs/DateTimePickerDialog';
import { GridTotalRow } from '../../sections/@dashboard/grid/total-row';
import { debounce } from 'lodash';
import { GridDataAdapter } from '../../adapter/DataAdapter';
import { formatBackendError, formatError } from '../../utils/formatError';
import { formatController } from '../../utils/formatName';
import FilterService from '../../services/filterService/FilterService';
import {
    getVisibleFieldSet,
    isFieldInVisibleSet,
    isFieldVisibleByRole
} from '../../utils/isFieldVisible';
import { archiveSupportConfig } from '../../config/archiveSupportConfig';
import { modifiedReasonConfig } from '../../config/modifiedReasonConfig';
import ModifiedDialog from '../dialogs/ModifiedDialog';
import CreateExportTaskDialog from '../dialogs/CreateExportTaskDialog';
import { EmptyStateInfoCard } from '../../components/EmptyStateInfoCard';
import { isCountDisabledFor } from '../../config/disableCount.config';
import TablePaginationActionsWithDisable from '../../components/table/TablePaginationActionsWithDisable';
import AddIcon from '@mui/icons-material/Add';
import { lockSupportConfig } from '../../config/lockSupportConfig';
import LockInstanceDialog from '../dialogs/LockInstanceDialog';

/**
 * @typedef {Object} HeadObject
 * @property {string} id - Unique identifier.
 * @property {string} label - Object label.
 * @property {boolean} alignRight - Alignment to the right.
 * @property {string} cell - Type of cell and editor.
 * @property {boolean} displayGrid - Display in the grid.
 * @property {boolean} disableEditor - Disable editor.
 * @property {boolean} displayDialogDetails - Display details in the dialog.
 * @property {'TextDetails' || 'Json'} details - Type of details:
 */

export default class GridPageAbstract extends React.Component {
    _isMounted = false;

    List = [];
    // Head = [];
    Sort = '';
    Name = 'Grid';
    Server = null;
    MenuDisplayType = 'menu' || 'icon' || 'none';
    CheckboxCell = true;
    ButtonRow = false;
    NeedSearch = true;

    disableGlobalEditor = false;
    disableGlobalDelete = false;
    disableAdd = false;

    searchFields = [];

    showDatePicker = true;
    // if enabled: grid head field must have a cell from cellConfig.js and in head object must be a field 'total' with true value
    showTotalRow = false;

    exportButton = false;

    unsubscribe = () => {};
    customLogicOnMount() {}
    hasCustomLogicOnMount = false;
    customPermissionCreate = null;

    allowedTransferBalance = () => false;
    transferBalance = () => {};

    disableDateRange = {
        disabled: false,
        text: ''
    };

    dateRangeField = 'dateCreated';
    limitDateRange = 31; // days
    disableCount = false;

    constructor(props) {
        super(props);
        this.tableViewportRef = React.createRef();
        this.state = {
            /**
             * Array of Head objects.
             * @type {HeadObject[]}
             */
            head: [],

            sortConfig: config.defaultSort,
            open: null,
            order: 'asc',
            selected: [],
            orderBy: 'name',
            searchValue: '',

            menuClickItemId: null,

            page: 0,
            rowsPerPage: 5,
            totalCount: 0,
            hasNextPage: true,

            dialogOpen: false,
            dialogData: {},

            isLoading: true,
            isDataLoading: false,
            filterRequest: [],

            dialogDetailsOpen: false,
            dialogSettingsOpen: false,
            selectedItem: {},

            confirmDialogOpen: false,
            confirmationText: '',

            openDateRange: false,
            dateRangeValue: '',

            modifiedDialogOpen: false,

            lockInstanceDialogOpen: false,

            permissions: this.props.context.getState().permissions || {},
            actions: this.props.context.getState().actions || {},
            controllersData:
                this.props.context.getState().controllersData || {},
            currentUserRole:
                this.props.context.getState().userInfo.role?.systemRole || '',
            csvExportEnableControllers: this.props.context.getState().csvExportEnableControllers || [],

            tableViewportWidth: 0,
            tableViewportHeight: 0
        };
        this.debouncedSearch = debounce(this.retrieveData, 500);
        this.fs = new FilterService();
    }

    installHeaderActions() {
        const isPermissionCreate = this.allowedPermission('create') && !this.disableAdd;
        const permissionsNotLoaded = !this.hasPermissions();

        const isMobile = window.innerWidth < 600;

        const titleNode = (
            <Typography
                variant="h5"
                gutterBottom
                color={'text.primary'}
                sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
            >
                {i18n.t(`pages.${this.Name}.title`)}
            </Typography>
        );

        const addButton =
            isPermissionCreate && !permissionsNotLoaded ? (
                <Button
                    variant="contained"
                    onClick={() => this.dialogOpen()}
                    startIcon={<AddIcon />}
                >
                    {isMobile ? i18n.t(`buttons.add`) : i18n.t(`pages.${this.Name}.add`)}
                </Button>
            ) : null;

        const actionsNode = (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ flexGrow: 1 }}
            >
                {titleNode}
                {addButton}
            </Stack>
        );
        this.props?.setHeaderActions?.(actionsNode);
    }

    uninstallHeaderActions() {
        this.props?.clearHeaderActions?.();
    }

    // utility to use setState with async/await
    setStateAsync(updater) {
        return new Promise((resolve) => {
            this.setState(updater, resolve);
        });
    }

    async componentDidMount() {
        this.disableCount = isCountDisabledFor(this.Name);

        this._isMounted = true;

        this.updateTableViewportSize();
        window.addEventListener('resize', this.updateTableViewportSize);

        const { context } = this.props;

        this.unsubscribe = context.subscribe(() => {
            const newPermissions = context.getState().permissions;
            const newActions = context.getState().actions;
            const newControllersData = context.getState().controllersData;
            const currentRole = context.getState().userInfo.role;
            const newCsvExportEnableControllers = context.getState().csvExportEnableControllers;
            if (this.state.permissions !== newPermissions && this._isMounted) {
                this.setState({
                    permissions: newPermissions,
                    actions: newActions,
                    controllersData: newControllersData,
                    currentUserRole: currentRole.systemRole,
                    csvExportEnableControllers: newCsvExportEnableControllers
                }, () => this.installHeaderActions());
            }
        });

        try {
            this.setState({ isDataLoading: false });

            await this.updateContextControllersData();

            if (this.hasCustomLogicOnMount) {
                await this.customLogicOnMount();
                console.log('Custom logic on mount executed');
            }

            await this.retrieveData();

        } catch (error) {
            console.error('Error in retrieveData:', error);
        } finally {
            if (this._isMounted) {
                this.setState({ isDataLoading: true }, () =>
                    this.installHeaderActions()
                );
            }
        }
    }

    componentWillUnmount() {
        this.unsubscribe();
        this._isMounted = false;
        window.removeEventListener('resize', this.updateTableViewportSize);
        this.uninstallHeaderActions();
    }

    updateTableViewportSize = () => {
        const el = this.tableViewportRef.current;
        if (!el) return;
        const width = el.clientWidth;
        const height = el.clientHeight;
        this.setState({
            tableViewportWidth: width,
            tableViewportHeight: height
        });
    };

    async fetchFilteredData(pageSize = null) {
        const sort = this.Server.getSortField(
            this.state.sortConfig.field,
            this.state.sortConfig.direction || 'desc'
        );
        const prefilter = this.buildFinalPrefilterRequest(this.fs);

        return await this.Server.getAllFiltered(
            this.state.filterRequest,
            sort,
            {
                paging: {
                    page: this.state.page,
                    pageSize: pageSize || this.state.rowsPerPage
                },
                prefilter,
                includeTotalCount: !this.disableCount
            }
        );
    }

    async updateContextControllersData() {
        try {
            const updates = {};
            let prefilter = [];
            try {
                await this.waitForPermissions();
                await this.waitForControllersData();
            } catch (error) {
                this.handleError(error);
                return;
            }
            const controllersData = this.state.controllersData;
            const uniqueFromFields = Array.from(
                new Map(
                    this.Head.filter(
                        (field) =>
                            field.from &&
                            this.allowedPermission('read', field.from)
                    ).map((field) => [field.from, field])
                ).values()
            );

            const fetchPromises = uniqueFromFields.map(async (field) => {
                if (this.entitySupportedArchive(field.object)) {
                    prefilter = [
                        {
                            field: 'isArchived',
                            op: '!=',
                            value: true
                        }
                    ];
                } else prefilter = [];

                const result = await this.props.context
                    .getState()
                    .server[field.from].getLight([], [], {
                        prefilter
                    });
                updates[field.from] = result.data;
            });

            await Promise.allSettled(fetchPromises);

            if (Object.keys(updates).length > 0) {
                this.props.context.update({
                    controllersData: {
                        ...controllersData,
                        ...updates
                    }
                });
            }
            this.installHeaderActions();
        } catch (error) {
            console.error(
                'An error occurred while updating context controllers data:',
                error
            );
        }
    }

    toggleArchived = async (isArchived) => {
        this.fs.clearGroup('prefilterBase');
        if (isArchived) {
            this.fs.addFilter('isArchived', '=', true, 'prefilterBase', true);
        } else {
            this.fs
                .clearGroup('prefilterBase')
                .addFilter('isArchived', '!=', true, 'prefilterBase', true);
        }
        await this.retrieveData();
    };

    processListData(data) {
        const uniqueItems = new Set();
        this.List = data.filter((item) => {
            if (!item.id) {
                return false;
            }
            const isUnique = !uniqueItems.has(item.id);
            if (isUnique) uniqueItems.add(item.id);
            return isUnique;
        });
    }

    handleError(error) {
        this.props.context.update({
            alertError: { message: formatError(error) }
        });
    }

    async retrieveData() {
        this.setState({ isLoading: true });

        try {
            let result;

            if (this.showTotalRow) {
                result = await this.fetchFilteredData(1000);
            } else {
                result = await this.fetchFilteredData();
            }

            if (result.error !== 'OK') {
                this.handleError(result);
                return;
            }

            this.processListData(result.data);

            if (this.disableCount) {
                this.setState({
                    hasNextPage: result.has_next
                    // hasNextPage: false
                });
            } else if (this.state.totalCount === 0) {
                this.setState({
                    hasNextPage: false
                });
            }

            if (this.state.page === 0) {
                const totalCount = this.disableCount ? -1 : result.total_count;
                this.setState({ totalCount: totalCount });
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    async updatePermissionsData() {
        const data = await this.props.context
            .getState()
            .server.users.metaInfo();
        this.props.context.update({
            permissions: data.permissions,
            actions: data.actionPermissions
        });
        this.installHeaderActions();
    }

    hasPermissions() {
        return !!Object.keys(this.state.permissions).length;
    }
    hasControllersData() {
        return !!Object.keys(this.state.controllersData).length;
    }

    waitForControllersData = (timeout = 5000) => {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();

            const checkControllersData = () => {
                if (this.hasControllersData()) {
                    resolve();
                } else if (Date.now() - startTime > timeout) {
                    reject(new Error('Controllers data loading timed out'));
                } else {
                    setTimeout(checkControllersData, 50);
                }
            };
            checkControllersData();
        });
    };

    waitForPermissions = (timeout = 5000) => {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();

            const checkPermissions = () => {
                if (this.hasPermissions()) {
                    resolve();
                } else if (Date.now() - startTime > timeout) {
                    reject(new Error('Permissions loading timed out'));
                } else {
                    setTimeout(checkPermissions, 50);
                }
            };
            checkPermissions();
        });
    };

    entitySupportedArchive = (entity) => {
        return archiveSupportConfig.some((cfg) => cfg.entity === entity);
    };

    allowedPermission = (action, controller = this.Name) => {
        const adaptedController = adaptedName(controller);
        const permission = this.hasPermissions()
            ? this.state.permissions?.[adaptedController]?.[action]
            : 'deny';
        return permission !== 'deny';
    };

    allowedAction = (action) => {
        if (!this.state.actions || Object.keys(this.state.actions).length === 0)
            return false;

        return this.state.actions[action] === 'allow';
    };

    allowedArchive = () => {
        const exists = archiveSupportConfig.some(
            (item) => item.controller === this.Name
        );
        return exists && this.allowedAction('archive');
    };

    allowedCsvExportEnableControllers = () => {
        const csvExportEnableControllers =
            this.state.csvExportEnableControllers;
        return csvExportEnableControllers.some(
            (item) => item === formatController(this.Name)
        );
    };

    allowedLock = () => {
        const allowed = this.allowedAction('lock-instances');
        const exists = lockSupportConfig.some((item) => item === this.Name);
        return allowed && exists;
    };

    shouldShowModifiedReasonDialog = () => {
        return modifiedReasonConfig.some((item) => item === this.Name);
    };

    handleExportDataToCsv = () => {
        this.setState({ createExportTaskDialogOpen: true });
    };

    buildFinalPrefilterRequest(fs) {
        const prefilterBase = fs.getFlatFiltersByGroup('prefilterBase');
        const prefilterDate = fs.getFlatFiltersByGroup('prefilterDate');
        return [...prefilterBase, ...prefilterDate];
    }

    search = async (value) => {
        const fs = this.fs;
        const hasValue = value !== '';

        if (!hasValue) {
            fs.clearGroup('search');
            const filterRequest = fs.getFiltersByGroup('search');
            this.setState({ filterRequest, searchValue: '' }, () =>
                this.retrieveData()
            );
            return;
        }

        const searchGroups = this.searchFields.map((field) => {
            return [{ field: field, op: ':', value: value }];
        });

        fs.clearGroup('search').addGroups(searchGroups, 'search');

        const filterRequest = fs.getFiltersByGroup('search');

        await this.setStateAsync({ page: 0, filterRequest, searchValue: value });
    };

    handleSearch = async (value) => {
        await this.search(value);
        await this.debouncedSearch();
    };


    filterItems = async (filters) => {
        const fs = this.fs;

        if (!filters || filters.length === 0) {
            fs.clearGroup('filter');
            const filterRequest = fs.getFiltersByGroup('filter');
            await this.setStateAsync({ filterRequest, filterActive: false, page: 0 }, () =>
                this.retrieveData()
            );
            return;
        }
        fs.clearGroup('filter').addGroup(filters, 'filter');

        const filterRequest = fs.getFiltersByGroup('filter');
        await this.setStateAsync({ filterRequest, filterActive: true, page: 0 });
    };

    handleFilterItems = async (filters) => {
        await this.filterItems(filters);
        await this.retrieveData();
    };
    async dateRangeSave(previewDate) {
        const fs = this.fs;
        const { startDate, endDate } = parseDate(previewDate);

        const prefilterDateGroup = formatFilterData(
            startDate,
            endDate,
            this.dateRangeField
        ).map(({ field, op, value }) => ({ field, op, value }));

        fs.clearGroup('prefilterDate').addGroup(
            prefilterDateGroup,
            'prefilterDate'
        );

        await this.setStateAsync(
            {
                openDateRange: false,
                dateRangeValue: previewDate
            }
        );
    }
    async handleDateRangeSave(previewDate) {
        await this.dateRangeSave(previewDate);
        await this.retrieveData();
    }

    handleClearDateRange() {
        const fs = this.fs;
        fs.clearGroup('prefilterDate');
        this.setState({ dateRangeValue: '' }, () => this.retrieveData());
    }

    handleLockInstance(bool) {
        // open lock dialog
        this.setState({ lockInstanceDialogOpen: bool });
    }

    handleOpenMenu = (event, id) => {
        this.setState({ open: event.currentTarget, menuClickItemId: id });
    };

    handleCloseMenu() {
        this.setState({ open: null, menuClickItemId: null });
    }

    handleRequestSort = (sortField) => {
        this.setState((prevState) => {
            const { field, direction } = prevState.sortConfig;
            const isFieldChanged = field !== sortField;

            const newDirection = isFieldChanged
                ? 'asc'
                : direction === 'asc'
                    ? 'desc'
                    : direction === 'desc'
                        ? this.state.sortConfig.direction ===
                      config.defaultSort.direction &&
                  this.state.sortConfig.field === config.defaultSort.field
                            ? 'asc'
                            : null
                        : 'asc';

            const newField = isFieldChanged
                ? sortField
                : newDirection === null
                    ? config.defaultSort.field
                    : sortField;

            return {
                sortConfig: {
                    field: newField,
                    direction: newDirection
                }
            };
        }, this.retrieveData);
    };

    handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = this.List.map((n) => n.id);
            this.setState({ selected: newSelected });
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event, name) => {
        const selectedIndex = this.state.selected.indexOf(name);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(this.state.selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(this.state.selected.slice(1));
        } else if (selectedIndex === this.state.selected.length - 1) {
            newSelected = newSelected.concat(this.state.selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                this.state.selected.slice(0, selectedIndex),
                this.state.selected.slice(selectedIndex + 1)
            );
        }
        this.setState({ selected: newSelected });
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage }, () => this.retrieveData());
    };

    handleChangeRowsPerPage = (event) => {
        this.setState(
            { page: 0, rowsPerPage: parseInt(event.target.value, 10) },
            () => this.retrieveData()
        );
    };

    handleEditItem = (event) => {
        const data = this.List.filter(
            (item) => item.id === this.state.menuClickItemId
        ).at(0);

        this.dialogOpen(data);
    };

    handleShowDialogDetails = (event) => {
        const data = this.List.filter(
            (item) => item.id === this.state.menuClickItemId
        ).at(0);
        this.setState({
            dialogDetailsOpen: true,
            selectedItem: data
        });
        this.handleCloseMenu();
    };
    handleGoToLinkedGrid(event, gridName, foreignKey) {
        let id = null;
        const data = this.List.filter(
            (item) => item.id === this.state.menuClickItemId
        ).at(0);
        if (data && data[foreignKey]) {
            id = data[foreignKey];
        }
        this.props.navigate(`/app/${gridName}`, { state: { searchValue: id } });
        this.handleCloseMenu();

    }

    handleCloseDialogDetails = () => {
        this.setState({
            dialogDetailsOpen: false,
            selectedItem: {}
        });
    };

    handleDeleteItem = async (event) => {
        // single delete from right click menu
        // or multi deleted from selected by checkbox items
        let result = await this.Server.delete(
            this.state.menuClickItemId
                ? [this.state.menuClickItemId]
                : this.state.selected
        );
        this.handleCloseConfirmDialog();
        if (result.error !== 'OK') {
            this.props.context.update({
                alertError: { message: result.error }
            });
        } else {
            await this.retrieveData([]);
        }
    };

    handleRefreshStatus(event) {
        const id = this.state.menuClickItemId;
        this.props.context.update({
            alertError: { message: 'Updating...', type: 'snackbar', severity: 'info' },
        });
        this.Server.refreshStatus(id).then((res) => {
            if (res.error === 'OK') {
                this.props.context.update({
                    alertError: { message: `Update successfully, status: ${res.data}`, type: 'snackbar', severity: 'success' },
                });
                this.retrieveData();
            } else {
                this.props.context.update({
                    alertError: { message: formatError(res), type: 'snackbar', severity: 'error' },
                });
            }

        });
        this.handleCloseMenu();
    }

    handleArchive = async () => {
        this.setState({ loading: true });

        const ids = this.state.menuClickItemId
            ? [this.state.menuClickItemId]
            : this.state.selected;

        try {
            const result = await this.Server.archive(ids);
            this.handleCloseConfirmDialog();
            if (result?.error === 'OK') {
                this.props.context.update({
                    alertError: {
                        message: i18n.t('errors.archiveSuccess'),
                        type: 'snackbar',
                        severity: 'success'
                    }
                });
                await this.retrieveData();
            } else {
                this.handleError(result);
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    handleSingleDeleteConfirmDialogOpen = (event) => {
        this.setState({
            open: false,
            confirmDialogOpen: true,
            confirmationText: i18n.t(
                `dialogs.${this.Name}ConfirmationSingleDelete`
            ),
            confirmActionType: 'delete'
        });
    };
    handleMultiDeleteConfirmDialogOpen = (event) => {
        this.setState({
            confirmDialogOpen: true,
            confirmationText: i18n.t(
                `dialogs.${this.Name}ConfirmationMultipleDeleteText`
            ),
            confirmActionType: 'delete'
        });
    };

    handleArchiveConfirmDialogOpen = (event) => {
        this.setState({
            confirmDialogOpen: true,
            confirmationText: i18n.t(`dialogs.confirmationArchiveText`),
            confirmActionType: 'archive',
            open: false
        });
    };

    handleCloseConfirmDialog = () => {
        this.setState({
            confirmDialogOpen: false,
            menuClickItemId: null,
            selected: []
        });
    };

    handleOpenDateRange = () => {
        // open date range picker
        this.setState({ openDateRange: true });
    };

    handleChangeRangeDateField = (dateRange) => {
        this.setState({ page: 0 });
        this.setState({ dateRangeValue: dateRange });
    };

    handleOpenDialogSettings = () => {
        this.setState({ dialogSettingsOpen: true });
    };

    handleCloseDialogSettings() {
        this.setState({ dialogSettingsOpen: false });
    }

    handleTableSettingsChange() {
        const head = JSON.parse(localStorage[`head_${this.Name}`]);
        this.setState({ head: head, dialogSettingsOpen: false });
    }
    getMenuClickItem() {
        const itemId = this.state.menuClickItemId;
        return this.List.find((item) => item.id === itemId);
    }
    getMenuClickItemValue(field) {
        const item = this.getMenuClickItem();
        return item ? item[field] : null;
    }
    handleAction = async (event, id, actionType) => {
        this.setState({ menuClickItemId: id }, () => {
            switch (actionType) {
            case 'edit':
                this.handleEditItem(event);
                break;
            case 'delete':
                this.handleSingleDeleteConfirmDialogOpen();
                break;
            default:
                break;
            }
        });
    };

    getRowsPerPageOptions = ({
        totalCount,
        hasNextPage,
        currentLen,
        rowsPerPage
    }) => {
        const baseOptions = [5, 10, 25];

        const ensureCurrentPresent = (opts) => {
            if (!opts.includes(rowsPerPage)) {
                const withCurrent = [...opts, rowsPerPage].sort(
                    (a, b) => a - b
                );
                return withCurrent.filter(
                    (v, i) => i === 0 || withCurrent[i - 1] !== v
                );
            }
            return opts;
        };

        const buildByCeil = (value) => {
            const ceil = baseOptions.find((v) => v >= value);
            // eslint-disable-next-line no-eq-null
            if (ceil == null) {
                return [...baseOptions];
            }
            return baseOptions.filter((v) => v <= ceil);
        };

        if (typeof totalCount === 'number' && totalCount >= 0) {
            const opts = buildByCeil(totalCount);
            return ensureCurrentPresent(opts);
        }

        if (hasNextPage === true) {
            return ensureCurrentPresent([...baseOptions]);
        }

        const opts = buildByCeil(currentLen || 0);
        return ensureCurrentPresent(opts);
    };

    renderCustomExtraIcons() {
        return <></>;
    }

    renderMenuCell(id) {
        return (
            <TableCell key="menu" align="right">
                {this.hasMenuItems(id) && (
                    <IconButton
                        size="large"
                        color="inherit"
                        onClick={(event) => this.handleOpenMenu(event, id)}
                    >
                        <Iconify icon={'eva:more-vertical-fill'} />
                    </IconButton>
                )}
            </TableCell>
        );
    }

    renderIconCell(id) {
        const isPermissionUpdate =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('update');
        const isPermissionDelete =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('delete');
        return (
            <TableCell key="menu" align="left">
                {this.hasMenuItems(id) && (
                    <ButtonGroup
                        sx={{ display: 'flex', justifyContent: 'flex-start' }}
                    >
                        {isPermissionUpdate && (
                            <Tooltip title={i18n.t('common.edit')}>
                                <IconButton
                                    onClick={(event) =>
                                        this.handleAction(event, id, 'edit')
                                    }
                                >
                                    <Iconify icon={'eva:edit-fill'} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isPermissionDelete && (
                            <Tooltip title={i18n.t('common.delete')}>
                                <IconButton
                                    onClick={(event) =>
                                        this.handleAction(event, id, 'delete')
                                    }
                                    sx={{ color: 'error.main' }}
                                >
                                    <Iconify icon={'eva:trash-2-outline'} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {this.renderCustomExtraIcons(id)}
                    </ButtonGroup>
                )}
            </TableCell>
        );
    }

    getCellValue = (row, field) => {
        if (field.array) return row[field.array];
        if (!field.object) {
            return row[field.id];
        }

        const baseObject = row?.[field.object];

        if (field.nestedObject) {
            const nestedObject = baseObject?.[field.nestedObject];

            if (field.secondaryObject) {
                const secondaryName = baseObject?.[field.secondaryObject]?.name;
                return { ...nestedObject, secondaryName };
            }

            return nestedObject;
        }

        return baseObject;
    };

    renderCustomGridCell() {
        return <></>;
    }

    hasMenuItems(id) {
        const customMenuItems = this.renderCustomMenuItems(id);
        const isPermissionUpdate =
            this.allowedPermission('update') && !this.disableGlobalEditor;
        const isPermissionDelete =
            this.allowedPermission('delete') && !this.disableGlobalDelete;

        return (
            isPermissionUpdate ||
            isPermissionDelete ||
            (Array.isArray(customMenuItems) && customMenuItems.length > 0)
        );
    }

    renderGridSingleRow(row) {
        const { id } = row;
        const selectedUser = this.state.selected.indexOf(id) !== -1;
        return (
            <TableRow
                hover
                key={id}
                tabIndex={-1}
                role="checkbox"
                selected={selectedUser}
                sx={this.ButtonRow ? { cursor: 'pointer' } : {}}
                onClick={
                    this.ButtonRow
                        ? (event) => {
                            if (event.target.type !== 'checkbox') {
                                this.handleCLickGridRow(event, id);
                            }
                        }
                        : null
                }
            >
                {this.renderCheckboxCell(id, selectedUser)}

                {this.state.head
                    .filter((f) => {
                        const fieldKey =
                            f.cell === 'SelectObject' ? f.object : f.id;
                        return (
                            f.displayGrid &&
                            isFieldVisibleByRole(
                                fieldKey,
                                this.state.currentUserRole
                            )
                        );
                    })
                    .map((field) => {
                        let Cell = Cells[field.cell];
                        if (!Cell) Cell = Cells.Text; // fallback to text cell

                        let cellValue = this.getCellValue(row, field);

                        if (field.customData) {
                            return this.renderCustomGridCell(Cell, row, field);
                        }

                        return (
                            <Cell
                                key={field.label}
                                id={row.id}
                                value={cellValue}
                                field={field}
                                row={row}
                                handleChange={this.handleChangeCell}
                                handleSave={this.handleSaveCell}
                                currentValues={this.state.currentValues}
                                context={this.props.context}
                                allowEdit={this.allowedPermission(
                                    'update',
                                    adaptedName(this.Name)
                                )}
                                styles={{
                                    padding: '8px'
                                }}
                            />
                        );
                    })}
                {this.renderMenuCell(id)}
            </TableRow>
        );
    }

    renderGridBodyRows(filteredUsers) {
        return (
            <>
                {filteredUsers.map((row) => {
                    return this.renderGridSingleRow(row);
                })}
            </>
        );
    }

    renderCheckboxCell(id, selectedUser) {
        return (
            <TableCell key="checkbox" padding="checkbox">
                <Checkbox
                    checked={selectedUser}
                    onChange={(event) => {
                        this.handleClick(event, id);
                    }}
                />
            </TableCell>
        );
    }

    renderGridBodyNotFound(colSpan = 99999) {
        const {
            searchValue,
            filterActive,
            tableViewportHeight,
            tableViewportWidth
        } = this.state;
        const notFoundSearch = !!searchValue && !filterActive;
        const notFoundFilter = !searchValue && filterActive;
        let icon = <InboxIcon fontSize="inherit" />;
        let title = i18n.t('text.noResults');
        let description = i18n.t('text.noResultsDesc');

        if (notFoundSearch) {
            icon = <SearchOffIcon fontSize="inherit" />;
            title = `${i18n.t('text.noResultsForSearch')}: "${searchValue}"`;
            description = `${i18n.t('text.noResultsSearchDesc')}.`;
        } else if (notFoundFilter) {
            icon = <FilterAltOffIcon fontSize="inherit" />;
            title = i18n.t('text.noResultsByFilter');
            description = i18n.t('text.noResultsByFilterDesc');
        }
        const viewportH = tableViewportHeight || 360;
        const viewportW = tableViewportWidth || 800;

        return (
            <TableRow>
                <TableCell
                    align="center"
                    colSpan={colSpan}
                    sx={{
                        position: 'relative',
                        height: '20px',
                        p: 0
                    }}
                >
                    <Box
                        sx={{
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            width: `${viewportW}px`,
                            minHeight: `${Math.max(viewportH - 48, 240)}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: 560 }}>
                            <EmptyStateInfoCard
                                icon={icon}
                                title={title}
                                description={description}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ height: 0 }} />
                </TableCell>
            </TableRow>
        );
    }

    renderCustomMenuItems(id) {
        return <></>;
    }

    renderMenuPopover(itemId) {
        const isPermissionUpdate =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('update') &&
            !this.disableGlobalEditor;
        const isPermissionDelete =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('delete') &&
            !this.disableGlobalDelete;
        const customMenuItems = this.renderCustomMenuItems(itemId);
        return (
            <>
                <Popover
                    key={itemId}
                    open={Boolean(this.state.open)}
                    anchorEl={this.state.open}
                    onClose={(event) => this.handleCloseMenu(event)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    // disablePortal
                    container={document.body}
                    slotProps={{
                        paper: {
                            sx: {
                                "p": 1,
                                "width": 200,
                                '& .MuiMenuItem-root': {
                                    px: 1,
                                    typography: 'body2',
                                    borderRadius: 0.75
                                }
                            }
                        }
                    }}
                >
                    {customMenuItems}

                    {isPermissionUpdate && (
                        <MenuItem
                            key={itemId + 'edit'}
                            onClick={(event) => this.handleEditItem(event)}
                        >
                            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
                            {i18n.t('buttons.edit')}
                        </MenuItem>
                    )}
                    {isPermissionDelete && (
                        <MenuItem
                            key={itemId + 'delete'}
                            onClick={(event) =>
                                this.handleSingleDeleteConfirmDialogOpen()
                            }
                            sx={{ color: 'error.main' }}
                        >
                            <Iconify
                                icon={'eva:trash-2-outline'}
                                sx={{ mr: 2 }}
                            />
                            {i18n.t('buttons.delete')}
                        </MenuItem>
                    )}
                </Popover>
            </>
        );
    }

    tableRowsLoading(rowCount, cellCount) {
        return [...Array(rowCount)].map((row, index) => (
            <TableRow key={index}>
                {[...Array(cellCount)].map((cell, i) => (
                    <TableCell component="th" scope="row" key={i}>
                        <Skeleton
                            animation="wave"
                            variant="text"
                            sx={{ my: 3 }}
                        />
                    </TableCell>
                ))}
            </TableRow>
        ));
    }

    renderCustom() {
        return <></>;
    }

    render() {
        const permissionsNotLoaded = !this.hasPermissions();
        const loading =
            this.state.isLoading ||
            !this.state.isDataLoading ||
            permissionsNotLoaded;
        const isNotFound = !this.List.length && !loading;

        const currentItemId = this.state.menuClickItemId;

        const visibleCellCount = this.state.head.filter((field) => {
            return (
                isFieldVisibleByRole(field.id, this.state.currentUserRole) &&
                field.displayGrid
            );
        }).length;

        const currentLength = this.List.length;

        return (
            <>
                {this.renderCustom()}

                {this.renderConfirmDialog()}

                {this.renderDialog()}

                {this.renderModifiedReasonDialog()}

                {this.renderDetailsDialogAbstract()}

                {this.renderSettingsDialog()}

                {this.renderDateRangePicker()}

                {this.renderCreateExportTaskDialog()}

                {this.renderLockInstanceDialog()}

                <Helmet>
                    <title>{i18n.t(`pages.${this.Name}.title`)}</title>
                </Helmet>

                <Container maxWidth={false}>
                    <Card>
                        <GridListToolbar
                            Head={this.state.head}
                            handleClickSelect={(event, name) =>
                                this.handleClick(event, name)
                            }
                            numSelected={this.state.selected.length}
                            searchValue={this.state.searchValue}
                            handleSearchData={this.handleSearch}
                            NeedSearch={this.NeedSearch}
                            handleFilterItems={(filter) =>
                                this.handleFilterItems(filter)
                            }
                            filterActive={this.state.filterActive}
                            filterRequest={this.fs.getFlatFiltersByGroup('filter')}
                            handleTableSettings={() =>
                                this.handleOpenDialogSettings()
                            }
                            onDelete={() =>
                                this.handleMultiDeleteConfirmDialogOpen()
                            }
                            deletePermission={this.allowedPermission('delete')}
                            archivePermission={this.allowedArchive()}
                            handleArchive={() =>
                                this.handleArchiveConfirmDialogOpen()
                            }
                            showDatePicker={this.showDatePicker}
                            handleOpenDateRange={this.handleOpenDateRange}
                            rangeValue={this.state.dateRangeValue}
                            limitDateRange={this.limitDateRange}
                            onRangeDate={this.handleChangeRangeDateField}
                            onClearRangeDate={() => this.handleClearDateRange()}
                            onSaveRangeDate={(rangeDate) =>
                                this.handleDateRangeSave(rangeDate)
                            }
                            disableDateRange={this.disableDateRange.disabled}
                            disableDateRangeText={this.disableDateRange.text}
                            exportDataToCsv={() => this.handleExportDataToCsv()}
                            exportButton={this.allowedCsvExportEnableControllers()}
                            params={this.props.params}
                            navigate={this.props.navigate}
                            controller={formatController(this.Name)}
                            toggleArchived={this.toggleArchived}
                            lockInstance={() => this.handleLockInstance(true)}
                            lockInstancePerm={this.allowedLock()}

                            transferBalancePerm={this.allowedTransferBalance()}
                            transferBalance={(direction) => this.transferBalance(direction)}
                        />

                        <Scrollbar>
                            <TableContainer
                                ref={this.tableViewportRef}
                                onScroll={this.updateTableViewportSize}
                                sx={{
                                    overflowX: 'initial',
                                    minWidth: 800
                                }}
                            >
                                <Table stickyHeader>
                                    <GridListHead
                                        order={this.state.sortConfig.direction}
                                        orderBy={this.state.sortConfig.field}
                                        headLabel={this.state.head}
                                        rowCount={this.List.length}
                                        numSelected={this.state.selected.length}
                                        onRequestSort={this.handleRequestSort}
                                        onSelectAllClick={
                                            this.handleSelectAllClick
                                        }
                                        checkboxCell={this.CheckboxCell}
                                        role={this.state.currentUserRole}
                                    />
                                    <TableBody>
                                        {loading ? (
                                            this.tableRowsLoading(
                                                5,
                                                visibleCellCount + 1
                                            )
                                        ) : (
                                            <>
                                                {this.renderGridBodyRows(
                                                    this.List
                                                )}
                                                {this.showTotalRow && (
                                                    <GridTotalRow
                                                        head={this.state.head}
                                                        data={this.List}
                                                        role={
                                                            this.state
                                                                .currentUserRole
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                        {isNotFound &&
                                            this.renderGridBodyNotFound()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Scrollbar>

                        {!this.showTotalRow && (
                            <TablePagination
                                rowsPerPageOptions={
                                    this.getRowsPerPageOptions({
                                        totalCount: this.state.totalCount,
                                        hasNextPage: this.state.hasNextPage,
                                        currentLen: currentLength,
                                        rowsPerPage: this.state.rowsPerPage
                                    }) || []
                                }
                                labelRowsPerPage={i18n.t('text.rowsPerPage')}
                                labelDisplayedRows={({ from, to, count }) => {
                                    if (count === -1) {
                                        return `${from}-${to}`;
                                    }
                                    return `${from}-${to} ${i18n.t(
                                        'text.of'
                                    )} ${count}`;
                                }}
                                component="div"
                                count={this.state.totalCount}
                                rowsPerPage={this.state.rowsPerPage}
                                page={this.state.page}
                                onPageChange={this.handleChangePage}
                                onRowsPerPageChange={
                                    this.handleChangeRowsPerPage
                                }
                                ActionsComponent={(subprops) => (
                                    <TablePaginationActionsWithDisable
                                        count={subprops.count}
                                        page={subprops.page}
                                        rowsPerPage={subprops.rowsPerPage}
                                        onPageChange={subprops.onPageChange}
                                        hasNextPage={this.state.hasNextPage}
                                    />
                                )}
                            />
                        )}
                    </Card>
                </Container>
                {this.renderMenuPopover(currentItemId)}
            </>
        );
    }

    dialogOpen(data) {
        const role = this.state.currentUserRole;
        const visibleFieldSet = getVisibleFieldSet(role);

        const dialogFields = this.state.head.filter((field) => {
            if (!isFieldInVisibleSet(field, visibleFieldSet)) return false;

            if (data) {
                return !field.disableEditor || field.displayOnEditForReadOnly;
            }
            return (
                !field.disableEditor ||
                (field.disableEditor && field.displayAdd)
            );
        });
        let dialogData = GridDataAdapter.transform(data, dialogFields);
        this.handleCloseMenu();
        this.setState({ dialogOpen: true, dialogData: dialogData });
    }

    async handleDialogOnSave(data, mode) {
        let result;
        if (mode === 'edit') {
            result = await this.Server.update(data.id, data);
        } else {
            result = await this.Server.add(data);
        }
        if (result.error === 'OK') {
            this.handleDialogOnClose();
        } else {
            this.setState({
                dialogError: formatBackendError(result)
            });
        }
        await this.retrieveData();
    }
    handleOpenModifiedReasonDialog = (data) => {
        this.setState({ modifiedDialogOpen: true, dialogData: data });
    };

    handleSubmitModifiedReasonDialog = async (modifiedReason) => {
        const data = {
            ...this.state.dialogData,
            modifiedReason
        };
        await this.handleDialogOnSave(data, 'edit');
        this.setState({ modifiedDialogOpen: false });
    };

    handleDialogOnClose() {
        this.handleCloseMenu();
        this.setState({
            dialogOpen: false,
            dialogData: null,
            dialogError: null
        });
    }

    renderConfirmDialog() {
        const { confirmDialogOpen, confirmationText } = this.state;
        const actionMap = {
            delete: this.handleDeleteItem,
            archive: this.handleArchive
        };
        return (
            <ConfirmDialog
                dialogOpen={confirmDialogOpen}
                onConfirm={async () => {
                    const action = actionMap[this.state.confirmActionType];
                    if (action) await action();
                    this.setState({ confirmDialogOpen: false });
                }}
                onCancel={() => {
                    this.handleCloseMenu();
                    this.setState({
                        confirmDialogOpen: false,
                        confirmationText: ''
                    });
                }}
                alertText={confirmationText}
                alertTitle={i18n.t('dialogs.confirmationTitle')}
            />
        );
    }

    renderDetailsDialogAbstract() {
        return (
            <DetailsDialogAbstract
                open={this.state.dialogDetailsOpen}
                onClose={() => this.handleCloseDialogDetails()}
                Head={this.state.head.filter((h) => h.displayDialogDetails)}
                context={this.props.context}
                data={this.state.selectedItem}
            />
        );
    }

    renderSettingsDialog() {
        return (
            <TableSettingsDialog
                open={this.state.dialogSettingsOpen}
                onClose={() => this.handleCloseDialogSettings()}
                Head={this.state.head.filter((h) => !h.displayDialogDetails)}
                Name={this.Name}
                onSave={this.handleTableSettingsChange.bind(this)}
            />
        );
    }

    renderDialog() {
        const needMR = this.shouldShowModifiedReasonDialog();
        return (
            <DialogAbstract
                dialogOpen={this.state.dialogOpen}
                dialogData={this.state.dialogData}
                role={this.state.currentUserRole}
                error={this.state.dialogError}
                Name={this.Name}
                Head={this.state.head}
                Server={this.Server}
                onSave={(data, mode) =>
                    needMR && mode === 'edit'
                        ? this.handleOpenModifiedReasonDialog(data)
                        : this.handleDialogOnSave(data, mode)
                }
                onClose={() => this.handleDialogOnClose()}
            />
        );
    }

    renderDateRangePicker() {
        return (
            <DateTimePickerDialog
                limit={this.limitDateRange}
                dialogOpen={this.state.openDateRange}
                value={this.state.dateRangeValue}
                onClose={() => this.setState({ openDateRange: false })}
                onSave={(dateRange) => this.handleDateRangeSave(dateRange)}
            />
        );
    }
    renderModifiedReasonDialog() {
        return (
            <ModifiedDialog
                open={this.state.modifiedDialogOpen}
                onCancel={() => this.setState({ modifiedDialogOpen: false })}
                onSubmit={async (reason) =>
                    await this.handleSubmitModifiedReasonDialog(reason)
                }
            />
        );
    }
    renderLockInstanceDialog() {
        return (
            <LockInstanceDialog
                open={this.state.lockInstanceDialogOpen}
                server={this.Server}
                onExit={() => this.handleLockInstance(false)}
            />
        );
    }
    renderCreateExportTaskDialog() {
        if (!this.state.createExportTaskDialogOpen) return null;

        const { startDate, endDate } = parseDate(this.state.dateRangeValue);

        const filterRequest = this.fs.getFiltersByGroup('filter');
        const searchRequest = this.fs.getFiltersByGroup('search');
        const prefilterRequest = this.fs.getFlatFiltersByGroup('prefilterBase');
        return (
            <CreateExportTaskDialog
                open={this.state.createExportTaskDialogOpen}
                onClose={() =>
                    this.setState({ createExportTaskDialogOpen: false })
                }
                server={this.Server}
                defaultFrom={startDate}
                defaultTo={endDate}
                filterRequest={filterRequest}
                searchRequest={searchRequest}
                prefilterRequest={prefilterRequest}
                onSuccess={(task) => {
                    this.props.context.update({
                        alertError: {
                            message: i18n.t('exportTask.created'),
                            type: 'snackbar',
                            severity: 'success'
                        }
                    });
                }}
            />
        );
    }
}
